import { fetchCurrentUser } from "@js/apps/auth/actions";
import { useUser } from "@js/apps/common/hooks/use-user";
import type { TaxFormType } from "@js/apps/freelancer/api";
import {
  useDeleteTaxFormMutation,
  useGetTaxFormQuery,
} from "@js/apps/freelancer/api";
import { useFreelancerProfile } from "@js/apps/freelancer/hooks/freelancer-profile";
import { ModalConfirm, ModalInstance } from "@js/components/modal";
import { Snackbar } from "@js/components/snackbar";
import { useAppDispatch } from "@js/hooks";

export const FORM_TYPE = {
  W8: "W8",
  W9: "W9",
} as const;

export type FormType = ObjectValues<typeof FORM_TYPE>;

export const useTaxDocuments = () => {
  const dispatch = useAppDispatch();
  const user = useUser();
  const freelancerProfile = useFreelancerProfile();

  const country = user?.address?.country;

  const isW8FormRequired =
    freelancerProfile?.tax_form_type === ENUMS.TaxForm.W8_FORM ||
    !!user?.is_w8_form_required;
  const isW9FormRequired =
    freelancerProfile?.tax_form_type === ENUMS.TaxForm.W9_FORM ||
    !!user?.is_w9_form_required;

  const freelancerId = user?.freelancer;

  const { data: w8TaxFormData, isLoading: isLoadingW8TaxForm } =
    useGetTaxFormQuery(
      { freelancerId: freelancerId as number, formType: "w8" },
      { skip: !isW8FormRequired },
    );
  const { data: w9TaxFormData, isLoading: isLoadingW9TaxForm } =
    useGetTaxFormQuery(
      { freelancerId: freelancerId as number, formType: "w9" },
      { skip: !isW9FormRequired },
    );

  const [deleteTaxForm] = useDeleteTaxFormMutation();

  const { form_uploaded_at: w9FormUploadedAt, form_url: w9FormUrl } =
    w9TaxFormData ?? {};
  const { form_uploaded_at: w8FormUploadedAt, form_url: w8FormUrl } =
    w8TaxFormData ?? {};

  const deleteTaxDocument = (type: TaxFormType) => {
    if (!freelancerId) {
      return;
    }

    return deleteTaxForm({ freelancerId, formType: type }).then(() => {
      dispatch(fetchCurrentUser()).catch(() => null);
      ModalInstance.close();
      Snackbar.success(`Deleted Successfully.`);
    });
  };

  const openDeleteTaxDocumentConfirmation = (type: TaxFormType) =>
    ModalInstance.open({
      children: (
        <ModalConfirm
          onCancel={ModalInstance.close}
          confirmText="Delete"
          onConfirm={() => deleteTaxDocument(type)}
        >
          Are you sure you want to delete{" "}
          <span className="capitalize">{type}</span> form?
        </ModalConfirm>
      ),
    });

  const deleteW8Form = () => openDeleteTaxDocumentConfirmation("w8");
  const deleteW9Form = () => openDeleteTaxDocumentConfirmation("w9");

  const returnForFormType = <T,>({
    w8,
    w9,
  }: Record<"w8" | "w9", T>): T | null => {
    if (isW8FormRequired) {
      return w8;
    }

    if (isW9FormRequired) {
      return w9;
    }

    return null;
  };

  const formTypeName = returnForFormType({
    w8: FORM_TYPE.W8,
    w9: FORM_TYPE.W9,
  });
  const isFormRequired = returnForFormType({
    w8: isW8FormRequired,
    w9: isW9FormRequired,
  });
  const uploadedAt = returnForFormType({
    w8: w8FormUploadedAt,
    w9: w9FormUploadedAt,
  });
  const uploadedFormUrl = returnForFormType({
    w8: w8FormUrl,
    w9: w9FormUrl,
  });
  const deleteForm = returnForFormType({
    w8: deleteW8Form,
    w9: deleteW9Form,
  });

  return {
    country,
    isW8FormRequired,
    isW9FormRequired,
    isLoading: isLoadingW8TaxForm || isLoadingW9TaxForm,
    deleteForm,
    isFormRequired,
    uploadedAt,
    uploadedFormUrl,
    returnForFormType,
    formTypeName,
  };
};
